import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Button } from '../Button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../DropdownMenu';
import { FieldLabelRow } from '../Field';
import { Icon } from '../Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Text } from '../Text';

type ComboBoxOption = {
  value: string;
  label: string;
  description?: string;
};

type ComboBoxProps = {
  options: ComboBoxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  /** Fires when focus leaves the closed trigger and when the popup closes, so forms can treat the field as touched. */
  onBlur?: () => void;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  allowDeselect?: boolean;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  label?: string;
  isRequired?: boolean;
  tooltipText?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  labelTestId?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
};

type PopoverSide = 'top' | 'bottom' | null;

/**
 * Searchable single-value picker for a long or data-driven list: users, agencies, counties, templates, anything
 * over roughly 10 options, or options that need a description line. For a short, fixed list the user recognizes
 * by name (statuses, yes/no, units) use `Select`, which is lighter and has no search box. A field that appears in
 * several places uses the same control in all of them.
 */
function ComboBox({
  options,
  value,
  onValueChange,
  onBlur,
  placeholder = 'Select option...',
  emptyText = 'No option found.',
  searchPlaceholder = 'Search...',
  isSearchable = true,
  isClearable = false,
  allowDeselect = true,
  disabled,
  className,
  triggerClassName,
  id: idProp,
  ref,
  label,
  isRequired,
  tooltipText,
  secondaryAction,
  labelTestId,
  'aria-label': ariaLabelProp,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'data-testid': testId,
}: ComboBoxProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(value || '');
  const [side, setSide] = React.useState<PopoverSide>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const observerRef = React.useRef<MutationObserver | null>(null);
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const ariaLabel = label || ariaLabelProp;

  const currentValue = value !== undefined ? value : internalValue;
  const selectedOption = options.find((option) => option.value === currentValue);
  const showClear = isClearable && !disabled && Boolean(currentValue);
  // Default to 'bottom' when open so rounding applies before content reports side
  const effectiveSide = side ?? 'bottom';

  // Filter on what users see rather than the option value
  const filterByLabel = React.useCallback(
    (itemValue: string, search: string) => {
      const option = options.find((o) => o.value === itemValue);
      const haystack = `${option?.label ?? ''} ${option?.description ?? ''}`.toLowerCase();
      const searchNorm = search.toLowerCase().trim();
      return haystack.includes(searchNorm) ? 1 : 0;
    },
    [options],
  );

  const contentRefCallback = React.useCallback((el: HTMLDivElement | null) => {
    contentRef.current = el;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    setSide(null);
    if (el) {
      const applySide = () => {
        const value = el.getAttribute('data-side');
        if (value === 'top' || value === 'bottom') setSide(value);
      };
      applySide();
      const observer = new MutationObserver(applySide);
      observer.observe(el, { attributes: true, attributeFilter: ['data-side'] });
      observerRef.current = observer;
    }
  }, []);

  const commitValue = (newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) onBlur?.();
  };

  const handleSelect = (selectedValue: string) => {
    commitValue(allowDeselect && selectedValue === currentValue ? '' : selectedValue);
    handleOpenChange(false);
  };

  const trigger = (
    <PopoverTrigger asChild>
      <button
        ref={ref}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label ? undefined : ariaLabelProp}
        aria-required={isRequired || undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        data-testid={testId}
        onBlur={() => {
          if (!open) onBlur?.();
        }}
        className={cn(
          'h-7 w-full min-w-0 rounded border bg-white px-2 text-xs leading-[1.4] text-grey-80 transition-[color,box-shadow,border-color] outline-none',
          'border-grey-30',
          'focus-visible:border-focus',
          'data-[state=open]:border-blue-50',
          // When dropdown opens down: flatten bottom corners on trigger to meet content
          effectiveSide === 'bottom' && 'data-[state=open]:rounded-b-none data-[state=open]:rounded-t',
          // When dropdown opens up: flatten top corners, keep bottom rounded to meet content above
          effectiveSide === 'top' && 'data-[state=open]:rounded-t-none data-[state=open]:rounded-b',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-10',
          'aria-invalid:border-destructive',
          'dark:bg-white/30',
          'flex items-center justify-between gap-2',
          currentValue ? '' : 'text-grey-50',
          'w-[180px]',
          isClearable && 'w-full',
          triggerClassName,
        )}
        type="button"
      >
        <span className={cn('flex min-w-0 items-baseline gap-1 truncate', showClear && 'mr-5')}>
          {selectedOption ? (
            <>
              <Text className="max-w-full shrink-0 text-xs truncate">{selectedOption.label}</Text>
              {selectedOption.description && (
                <Text className="min-w-0 text-xs text-grey-50 truncate">{selectedOption.description}</Text>
              )}
            </>
          ) : (
            <Text className="text-xs truncate text-grey-50">{placeholder}</Text>
          )}
        </span>
        <Icon icon="chevronDown" size={16} className="shrink-0 opacity-50" />
      </button>
    </PopoverTrigger>
  );

  const control = (
    // modal={true} is required for scrolling to work inside the popover
    // see: https://github.com/shadcn-ui/ui/issues/542 and https://github.com/shadcn-ui/ui/issues/607
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      {isClearable ? (
        <div className="relative flex w-full min-w-0 items-center">
          {trigger}
          {showClear && (
            <button
              type="button"
              aria-label={`Clear ${ariaLabel ?? 'selection'}`}
              data-testid={testId ? `${testId}-clear` : undefined}
              className="absolute right-7 flex size-4 cursor-pointer items-center justify-center rounded text-grey-80 hover:bg-grey-10 focus-ring"
              onClick={() => commitValue('')}
            >
              <Icon icon="cross2" size={14} />
            </button>
          )}
        </div>
      ) : (
        trigger
      )}
      <PopoverContent
        ref={contentRefCallback}
        className={cn(
          'min-w-(--radix-popover-trigger-width) p-0 border border-blue-50 bg-white shadow-md overflow-hidden',
          effectiveSide === 'bottom' && 'rounded-t-none rounded-b',
          effectiveSide === 'top' && 'rounded-t rounded-b-none',
          className,
        )}
        align="start"
        sideOffset={-1}
      >
        <Command
          className={cn('min-w-full flex flex-col', effectiveSide === 'bottom' ? 'rounded-b' : 'rounded-t')}
          filter={filterByLabel}
          defaultValue={currentValue || undefined}
        >
          <div className={effectiveSide === 'top' ? 'order-2' : undefined}>
            {isSearchable && (
              <CommandInput
                placeholder={searchPlaceholder}
                borderColor="border-blue-50"
                wrapperClassName={cn(
                  effectiveSide === 'bottom' && 'rounded-t-none border-t-0',
                  effectiveSide === 'top' && '!border-b-0 border-t border-blue-50 rounded-b-none',
                )}
              />
            )}
          </div>
          <div className={cn('min-h-0 flex-1 overflow-hidden', effectiveSide === 'top' && 'order-1')}>
            <CommandList label={ariaLabel}>
              <CommandEmpty>
                <Text className="text-xs">{emptyText}</Text>
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={handleSelect}
                    data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                  >
                    <span className="flex min-w-0 items-baseline gap-1 truncate">
                      <Text className="max-w-full shrink-0 text-xs truncate">{option.label}</Text>
                      {option.description && (
                        <Text className="min-w-0 text-xs text-grey-50 truncate">{option.description}</Text>
                      )}
                    </span>
                    <Icon
                      icon="check"
                      size={16}
                      className={cn('ml-auto shrink-0', currentValue === option.value ? 'opacity-100' : 'opacity-0')}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );

  if (!label) return control;

  return (
    // Not `Field`: a label and one control need no group role, and it would nest inside FormItem's group.
    <div className="flex w-full flex-col gap-1 [&>*]:w-full">
      <FieldLabelRow
        htmlFor={id}
        isRequired={isRequired}
        tooltipText={tooltipText}
        secondaryAction={secondaryAction}
        tooltipTestId={testId ? `${testId}-tooltip` : undefined}
        data-testid={labelTestId}
      >
        {label}
      </FieldLabelRow>
      {control}
    </div>
  );
}

type ComboBoxPopoverProps = {
  options: ComboBoxOption[];
  value?: ComboBoxOption | null;
  onValueChange?: (value: ComboBoxOption | null) => void;
  label?: string;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  triggerClassName?: string;
};

/**
 * ComboBox with Popover trigger for setting status or similar use cases
 */
function ComboBoxPopover({
  options,
  value,
  onValueChange,
  label,
  placeholder = '+ Set status',
  emptyText = 'No results found.',
  searchPlaceholder = 'Change status...',
  side = 'right',
  align = 'start',
  className,
  triggerClassName,
}: ComboBoxPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<ComboBoxOption | null>(value || null);

  const currentValue = value !== undefined ? value : internalValue;

  const handleSelect = (selectedValue: string) => {
    const selected = options.find((option) => option.value === selectedValue) || null;
    if (value === undefined) {
      setInternalValue(selected);
    }
    onValueChange?.(selected);
    setOpen(false);
  };

  return (
    <div className="flex items-center space-x-4">
      {label && <p className="text-grey-50 text-sm">{label}</p>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            role="combobox"
            aria-expanded={open}
            className={cn(
              'h-7 w-full min-w-0 rounded border bg-white px-2 text-xs leading-[1.4] text-grey-80 transition-[color,box-shadow,border-color] outline-none',
              'border-grey-30',
              'focus-visible:border-focus',
              'data-[state=open]:border-blue-50 data-[state=open]:rounded-b-none',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-10',
              'dark:bg-white/30',
              'flex items-center justify-between gap-2',
              currentValue ? '' : 'text-grey-50',
              'w-[150px] justify-start',
              triggerClassName,
            )}
            type="button"
          >
            <Text className="text-xs truncate">{currentValue ? currentValue.label : placeholder}</Text>
            <Icon icon="chevronDown" size={16} className="shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('p-0 border-blue-50 rounded-t-none rounded-b', className)}
          side={side}
          align={align}
          sideOffset={0}
        >
          <Command className="rounded-b">
            <CommandInput placeholder={searchPlaceholder} borderColor="border-blue-50" />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem key={option.value} value={option.value} onSelect={handleSelect}>
                    <Text className="text-xs">{option.label}</Text>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

type ComboBoxDropdownMenuProps = {
  options: string[];
  value?: string;
  onValueChange?: (value: string) => void;
  trigger?: React.ReactNode;
  label?: string;
  description?: React.ReactNode;
  emptyText?: string;
  searchPlaceholder?: string;
  subMenuLabel?: string;
  align?: 'start' | 'center' | 'end';
  className?: string;
  beforeSubMenu?: React.ReactNode;
  afterSubMenu?: React.ReactNode;
};

/**
 * ComboBox integrated with DropdownMenu for contextual actions
 */
function ComboBoxDropdownMenu({
  options,
  value,
  onValueChange,
  trigger,
  label,
  description,
  emptyText = 'No label found.',
  searchPlaceholder = 'Filter label...',
  subMenuLabel = 'Apply label',
  align = 'end',
  className,
  beforeSubMenu,
  afterSubMenu,
}: ComboBoxDropdownMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(value || options[0] || '');

  const _currentValue = value !== undefined ? value : internalValue;

  const handleSelect = (selectedValue: string) => {
    if (value === undefined) {
      setInternalValue(selectedValue);
    }
    onValueChange?.(selectedValue);
    setOpen(false);
  };

  return (
    <div className="flex w-full flex-col items-start justify-between rounded-md border border-grey-30 px-4 py-3 sm:flex-row sm:items-center">
      <p className="text-sm leading-none font-medium">
        {label && <span className="bg-primary text-white mr-2 rounded-lg px-2 py-1 text-xs">{label}</span>}
        {description && <span className="text-grey-50">{description}</span>}
      </p>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <Button variant="hollow" size="small" aria-label={label ? `Actions for ${label}` : 'Actions'}>
              <Icon icon="dotsHorizontal" size={16} />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className={cn('w-50', className)}>
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuGroup>
            {beforeSubMenu}
            {beforeSubMenu && <DropdownMenuSeparator />}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{subMenuLabel}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-0 border-blue-50 rounded-t-none rounded-b">
                <Command className="rounded-b">
                  <CommandInput placeholder={searchPlaceholder} autoFocus={true} borderColor="border-blue-50" />
                  <CommandList>
                    <CommandEmpty>{emptyText}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => (
                        <CommandItem key={option} value={option} onSelect={handleSelect}>
                          <Text className="text-xs truncate">{option}</Text>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            {afterSubMenu && (
              <>
                <DropdownMenuSeparator />
                {afterSubMenu}
              </>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export { ComboBox, ComboBoxPopover, ComboBoxDropdownMenu };
export type { ComboBoxOption, ComboBoxProps, ComboBoxPopoverProps, ComboBoxDropdownMenuProps };
